// apps/web/src/pages/coletor/kanban-tablet/tablet-cartao.tsx — Fase 11, Plano 15 (KAN-06, D-50).
// Cartão do tablet: só o estado atual e o motivo de pausa/bloqueio — sem histórico.
import type { ReactElement, ReactNode } from 'react';
import { Ban, CheckCircle2, CircleDot, Clock, PauseCircle, RotateCcw, type LucideIcon } from 'lucide-react';
import type { EstadoCartaoKanban, KanbanCartaoTablet } from '@pieltec/contracts';

const ESTADOS: Record<EstadoCartaoKanban, { texto: string; Icone: LucideIcon; classes: string }> = {
  EM_PRODUCAO: { texto: 'Em produção', Icone: CircleDot, classes: 'bg-emerald-700/25 text-blue-200 border-blue-400/60' },
  AGUARDANDO: { texto: 'Aguardando', Icone: Clock, classes: 'bg-slate-800 text-slate-200 border-slate-600' },
  PAUSADO: { texto: 'Pausado', Icone: PauseCircle, classes: 'bg-amber-500/20 text-amber-200 border-amber-400/60' },
  BLOQUEADO: { texto: 'Bloqueado', Icone: Ban, classes: 'bg-rose-600/25 text-rose-200 border-rose-400/60' },
  RETRABALHO: { texto: 'Retrabalho', Icone: RotateCcw, classes: 'bg-purple-600/25 text-purple-200 border-purple-400/60' },
  CONCLUIDO: { texto: 'Concluído', Icone: CheckCircle2, classes: 'bg-emerald-600/25 text-emerald-200 border-emerald-400/60' },
};

export interface TabletCartaoProps {
  cartao: KanbanCartaoTablet;
  acoes?: ReactNode;
}

export function TabletCartao({ cartao, acoes }: TabletCartaoProps): ReactElement {
  const estado = cartao.bloqueio ? ESTADOS.BLOQUEADO : ESTADOS[cartao.estado];
  const Icone = estado.Icone;
  const emProducao = cartao.estado === 'EM_PRODUCAO';
  const motivo = cartao.bloqueio ? cartao.bloqueio.motivo : cartao.estado === 'PAUSADO' ? cartao.motivoPausa : null;

  return (
    <article
      data-testid="tablet-cartao"
      data-estado={cartao.estado}
      className={`rounded-2xl border p-5 space-y-3 shadow-lg ${
        emProducao ? 'bg-blue-950/50 border-blue-400 ring-2 ring-emerald-400/60' : 'bg-slate-900 border-slate-800'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 bg-emerald-700/30 border border-emerald-500/40 text-blue-300 text-xs font-mono font-bold rounded">
            OP-{cartao.opNumero}
          </span>
          <span className="text-xs font-mono text-slate-400">Lote {cartao.loteCodigo}</span>
          {cartao.retrabalho && <span className="text-xs font-bold text-purple-300">Retrabalho</span>}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 border rounded-full text-xs font-bold ${estado.classes}`}>
          <Icone className="h-4 w-4" aria-hidden="true" />
          {estado.texto}
        </span>
      </div>

      <div>
        <p className="text-lg font-bold text-white">{cartao.itemNome}</p>
        <p className="text-sm text-slate-300">
          {cartao.clienteNome}
          {cartao.corCodigo ? ` · Cor ${cartao.corCodigo}` : ''}
        </p>
      </div>

      <p className="text-base font-mono text-slate-100" data-testid="tablet-cartao-progresso">
        {cartao.quantidadeProduzida}/{cartao.quantidadeAlvo}
      </p>

      {motivo && (
        <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
          {motivo}
        </p>
      )}

      {acoes && <div className="flex flex-wrap gap-3 pt-1">{acoes}</div>}
    </article>
  );
}
